import { z } from 'zod';

import { pageMetaSchema } from '@/lib/api/pagination';

// Type-only (circular) import: the inferred form types live in `types.ts`, which infers them
// from the applicability schemas below. The default factories reference those inferred types.
import type { ApplicabilityFormValue, ApplicableToFormValue } from './types';

// --- Async-multiselect picker schemas (ticket 08) ---
/** Shared cursor envelope for the meta pickers: `nextCursor` present only when `hasNextPage`. */
const cursorMetaSchema = z.object({
  nextCursor: z.string().optional(),
  hasNextPage: z.boolean(),
});

const cursorPageSchema = <T extends z.ZodTypeAny>(item: T) =>
  z.object({ data: z.array(item), meta: cursorMetaSchema });

export const metaOptionSchema = z.object({ id: z.string(), name: z.string() });
/** Unified device picker option: standalone devices + standalone-sold components; `isComponent`
 *  distinguishes them (bundle-only components are excluded server-side). */
export const deviceMetaOptionSchema = metaOptionSchema.extend({ isComponent: z.boolean() });

export const customerMetaPageSchema = cursorPageSchema(metaOptionSchema);
export const bundleMetaPageSchema = cursorPageSchema(metaOptionSchema);
export const deviceMetaPageSchema = cursorPageSchema(deviceMetaOptionSchema);

// --- Discount domain schemas ---
/** Wire enums — source of truth is the Postman contract (grilling.md → API contract). */
export const discountFormatSchema = z.enum(['flat_amount', 'percentage']);
export const applicableToSchema = z.enum(['device', 'bundle', 'customer']);
export const discountStatusSchema = z.enum(['active', 'inactive', 'expired']);

/** Relationship-tab wire enums (ticket 06). `tabs[]` on the detail lists which of these the
 *  discount exposes; each item shape is validated per-type via {@link ENTITY_ITEM_SCHEMA}. */
export const applicableEntityTypeSchema = z.enum(['customer', 'device', 'bundle']);
/** Detail `tabs` come back as PLURAL wire tokens (`customers`/`devices`/`bundles`); map them to
 *  the singular `ApplicableEntityType` the relationship tabs + `applicable-entities?type=` use. */
const TAB_TOKEN_TO_TYPE = { customers: 'customer', devices: 'device', bundles: 'bundle' } as const;
export const discountTabSchema = z
  .enum(['customers', 'devices', 'bundles'])
  .transform((t) => TAB_TOKEN_TO_TYPE[t]);
/** Per-Customer redemption state on the discount detail Customers tab (see CONTEXT.md). */
export const redemptionStatusSchema = z.enum(['used', 'not_used']);
export const bundleTierSchema = z.enum(['basic', 'advance', 'advance_plus']);

export const customerEntitySchema = z.object({
  id: z.string(),
  name: z.string(),
  phone: z.string(),
  status: redemptionStatusSchema,
});
export const deviceEntitySchema = z.object({
  id: z.string(),
  name: z.string(),
  category: z.string(),
  imageUrl: z.string().nullable(),
});
export const bundleEntitySchema = z.object({
  id: z.string(),
  name: z.string(),
  tier: bundleTierSchema,
  imageUrl: z.string().nullable(),
});

/** Item schema keyed by tab type — the api layer picks the right validator by `type`. */
export const ENTITY_ITEM_SCHEMA = {
  customer: customerEntitySchema,
  device: deviceEntitySchema,
  bundle: bundleEntitySchema,
} as const;

/** One row on the discounts listing (`GET /discounts/admin`). `maximumDiscountLimit` is the
 *  backend-computed value the "Maximum Discount Limit" column renders (currency for flat,
 *  percent for percentage — see format.ts). `applicableTo` is the top-level applicability type. */
export const discountRowSchema = z.object({
  id: z.string(),
  name: z.string(),
  discountFormat: discountFormatSchema,
  maximumDiscountLimit: z.number(),
  applicableTo: applicableToSchema,
  createdDate: z.string(),
  status: discountStatusSchema,
});

export const discountListResponseSchema = z.object({
  data: z.array(discountRowSchema),
  meta: pageMetaSchema,
});

/** Trigger types — when a discount can be used (see CONTEXT.md → Trigger). */
export const triggerTypeSchema = z.enum([
  'once_per_customer',
  'limited_period',
  'first_time_customer',
]);
/** Quantity-rule types — fixed value vs per-quantity tiers (see CONTEXT.md → Quantity Rule). */
export const quantityRuleTypeSchema = z.enum(['fixed', 'quantity_based']);

const dateRangeSchema = z.object({ from: z.string(), to: z.string() });
const detailTierSchema = z.object({
  minQty: z.number(),
  maxQty: z.number().nullable(),
  value: z.number(),
});

/** `GET /discounts/admin/:id` — the subset ticket 05 renders. Unknown keys (`tabs[]`, the full
 *  `applicability.ids`) are stripped by zod; ticket 06 extends this for the relationship tabs.
 *  `maxQty: null` = an open-ended top tier ("20+ units") — confirm the BE's open-tier convention. */
export const discountDetailSchema = z.object({
  id: z.string(),
  name: z.string(),
  discountFormat: discountFormatSchema,
  value: z.number(),
  description: z.string().nullable().optional(),
  status: discountStatusSchema,
  createdDate: z.string(),
  trigger: z.object({ type: triggerTypeSchema, dateRange: dateRangeSchema.nullable() }),
  quantityRule: z.object({
    type: quantityRuleTypeSchema,
    tiers: z.array(detailTierSchema).optional(),
  }),
  maximumDiscountLimit: z.number(),
  applicability: z.object({ summary: z.string() }),
  tabs: z.array(discountTabSchema),
});

/** RHF-draft validator for the filter drawer (ticket 03). Multi-selects are arrays; created-date
 *  bounds are nullable strings (a plain calendar range — no To>=From refine needed here). */
export const discountFiltersSchema = z.object({
  discountFormat: z.array(discountFormatSchema),
  applicableTo: z.array(applicableToSchema),
  status: z.array(discountStatusSchema),
  createdFrom: z.string().nullable(),
  createdTo: z.string().nullable(),
});

// --- Create-wizard schemas + input masks (tickets 109605/606/607) ---
export const DISCOUNT_NAME_MAX = 100;
export const DISCOUNT_DESCRIPTION_MAX = 2000;
export const DISCOUNT_AMOUNT_MAX = 999_999.99;
export const DISCOUNT_PERCENTAGE_MAX = 100;

// Alphanumeric, space, hyphen, underscore, and parentheses (AC 109605: Discount Name allowed chars).
const DISCOUNT_NAME_DISALLOWED = /[^A-Za-z0-9 _()-]/g;

export function maskDiscountName(raw: string): string {
  return raw.replace(DISCOUNT_NAME_DISALLOWED, '').slice(0, DISCOUNT_NAME_MAX);
}

/** Keep digits + one dot, cap to 2 decimals, then clamp to the per-format maximum. */
export function maskDiscountValue(raw: string, format: 'flat_amount' | 'percentage'): string {
  let cleaned = raw.replace(/[^0-9.]/g, '');
  const firstDot = cleaned.indexOf('.');
  if (firstDot !== -1) {
    cleaned = cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '');
    const [whole, decimals] = cleaned.split('.');
    cleaned = `${whole}.${decimals.slice(0, 2)}`;
  }
  if (cleaned === '' || cleaned === '.') return cleaned;
  const max = format === 'percentage' ? DISCOUNT_PERCENTAGE_MAX : DISCOUNT_AMOUNT_MAX;
  const n = Number(cleaned);
  if (!Number.isNaN(n) && n > max) {
    return cleaned.includes('.') ? String(max) : String(Math.trunc(max));
  }
  return cleaned;
}

// --- Create-wizard applicability (Step 1, ticket step1-applicability) ---
export const deviceRefSchema = z.object({ id: z.string(), isComponent: z.boolean() });

/** Empty-required picker messages — shared by the schema refine and the nested-picker error the
 *  form surfaces on sub-option reveal, so the two never drift. */
export const PICKER_REQUIRED_MESSAGE = {
  device: 'Please select at least one device',
  bundle: 'Please select at least one bundle',
  customer: 'Please select at least one customer',
} as const;

/** A picker is satisfied when "All" is on OR at least one id is chosen. */
const nonEmptyPicker = <T extends z.ZodTypeAny>(item: T, message: string) =>
  z
    .object({ all: z.boolean(), ids: z.array(item) })
    .refine((v) => v.all || v.ids.length > 0, { message });

const devicePicker = nonEmptyPicker(deviceRefSchema, PICKER_REQUIRED_MESSAGE.device);
const bundlePicker = nonEmptyPicker(z.string(), PICKER_REQUIRED_MESSAGE.bundle);
const customerPicker = nonEmptyPicker(z.string(), PICKER_REQUIRED_MESSAGE.customer);

/** Nested "Applicable To" sub-scope for customer applicability (renamed from applicableToSchema
 *  to free that name for the wire enum above). */
export const applicableToFormSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('all_products') }),
  z.object({ type: z.literal('device'), devices: devicePicker }),
  z.object({ type: z.literal('bundle'), bundles: bundlePicker }),
]);

export const applicabilitySchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('device'), devices: devicePicker }),
  z.object({ type: z.literal('bundle'), bundles: bundlePicker }),
  z.object({
    type: z.literal('customer'),
    customers: customerPicker,
    applicableTo: applicableToFormSchema,
  }),
]);

// Shared Step-1 field shape, reused by both the Step-1 gate schema and the full create schema.
const step1Fields = {
  name: z
    .string()
    .max(DISCOUNT_NAME_MAX)
    .superRefine((val, ctx) => {
      const trimmed = val.trim();
      if (trimmed.length === 0) {
        ctx.addIssue({
          code: 'custom',
          message: 'Discount Name cannot be empty or contain only spaces',
        });
      } else if (trimmed.length < 2) {
        ctx.addIssue({ code: 'custom', message: 'Discount Name must be at least 2 characters' });
      }
    }),
  discountFormat: discountFormatSchema,
  value: z.string().min(1, 'Required'),
  // Optional field: empty string is allowed (no `.min`). Kept required-key / no `.default`
  // so the zod input and output types match — the shared single-param `Control<T>` used by
  // <FormField>/<RadioGroupField> requires input == output. The RHF form seeds it to ''.
  description: z.string().max(DISCOUNT_DESCRIPTION_MAX),
  applicability: applicabilitySchema,
};

/** Discount amount/percentage bounds — reused by both step-1 and full-create schemas. */
function valueSuperRefine(
  v: { value: string; discountFormat: z.infer<typeof discountFormatSchema> },
  ctx: z.RefinementCtx,
): void {
  const n = Number(v.value);
  if (v.value === '' || Number.isNaN(n)) {
    ctx.addIssue({ code: 'custom', path: ['value'], message: 'Enter a valid amount' });
    return;
  }
  if (n <= 0) {
    ctx.addIssue({ code: 'custom', path: ['value'], message: 'Value must be greater than 0' });
  }
  const max = v.discountFormat === 'percentage' ? DISCOUNT_PERCENTAGE_MAX : DISCOUNT_AMOUNT_MAX;
  if (n > max) {
    ctx.addIssue({
      code: 'custom',
      path: ['value'],
      message:
        v.discountFormat === 'percentage'
          ? 'Percentage cannot exceed 100'
          : 'Amount cannot exceed 999,999.99',
    });
  }
}

/** Step 1 only — the Step-1 "Next" gate (safeParse on watched values). */
export const discountStep1Schema = z.object(step1Fields).superRefine(valueSuperRefine);

/** A discount window as `[from, to]` ISO date strings; `to` must be on or after `from`. */
export const dateRangeTuple = z
  .tuple([z.string().min(1), z.string().min(1)])
  .refine(([from, to]) => to >= from, { message: 'To Date must be on or after From Date' });

/** Both trigger arms carry a nullable window (the form holds `null` until a range is picked); the
 *  refine below enforces Limited Period's requirement, so the in-form value never needs a cast. */
const triggerWindow = dateRangeTuple.nullable();

/** Step-2 trigger (discriminated). Once = optional window; Limited = required window; First Time = none. */
export const triggerSchema = z
  .discriminatedUnion('type', [
    z.object({ type: z.literal('once_per_customer'), dateRange: triggerWindow }),
    z.object({ type: z.literal('limited_period'), dateRange: triggerWindow }),
    z.object({ type: z.literal('first_time_customer') }),
  ])
  .superRefine((t, ctx) => {
    if (t.type === 'limited_period' && t.dateRange == null) {
      ctx.addIssue({
        code: 'custom',
        path: ['dateRange'],
        message: 'Please select both From Date and To Date',
      });
    }
  });

export function defaultTrigger(): z.infer<typeof triggerSchema> {
  return { type: 'once_per_customer', dateRange: null };
}

/** Cross-field rule: First Time Customer is not offered for customer-scoped discounts. Shared by the
 *  create schema, the Step-2 Next-gate, and the wizard's breaking-change reset so they can't drift. */
export function isFirstTimeTriggerForCustomer(
  applicabilityType: ApplicabilityFormValue['type'],
  triggerType: z.infer<typeof triggerSchema>['type'],
): boolean {
  return applicabilityType === 'customer' && triggerType === 'first_time_customer';
}

// --- Step 3: Quantity Rule ---

/**
 * Ceiling for tier Min/Max Qty: **3 digits, 999** — the BA's own ruling on 117110 (18 Sep) and the
 * bound the deployed API enforces, probed live 2026-09-22 (`maxQty: 999` → 201; `maxQty: 1000` →
 * 400 `VALIDATION_FAILED`, "Min Qty and Max Qty must not exceed 999").
 *
 * Deliberately **not** `DISCOUNT_AMOUNT_MAX`, which it borrowed while no qty-specific max existed:
 * a quantity and a currency amount share no bound, and clamping at 999,999 is what let QA type six
 * digits and collect a server refusal the form could not render (117110, reopened).
 */
export const TIER_QTY_MAX = 999;

/** Integer-only mask for tier Min/Max: strip non-digits + leading zeros ('' for empty or
 *  all-zeros), then clamp to `TIER_QTY_MAX`. */
export function maskInteger(raw: string): string {
  const digits = raw.replace(/[^0-9]/g, '').replace(/^0+/, '');
  if (digits === '') return digits;
  return Number(digits) > TIER_QTY_MAX ? String(TIER_QTY_MAX) : digits;
}

/** One tier row (Min/Max qty + per-tier value). Held as strings to match the masked inputs. */
export const tierSchema = z.object({
  minQty: z.string().min(1, 'Required'),
  maxQty: z.string().min(1, 'Required'),
  value: z.string().min(1, 'Required'),
});

/** Tier behaviour: cap tier values at the Step-1 value, or let them exceed it. Single source of the
 *  union (derived `TierBehavior` type lives in types.ts). */
export const tierBehaviorSchema = z.enum(['limit_to_original', 'allow_higher']);

/** Step-3 quantity rule (discriminated): Fixed (no tiers) or Quantity-Based (behaviour + tiers). */
export const quantityRuleSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('fixed') }),
  z.object({
    type: z.literal('quantity_based'),
    tierBehavior: tierBehaviorSchema,
    tiers: z.array(tierSchema).min(1),
  }),
]);

export function defaultQuantityRule(): z.infer<typeof quantityRuleSchema> {
  return { type: 'fixed' };
}

/** Pre-filled first tier (Min 1 / Max 10 / the Step-1 value) — cannot be deleted in the editor. */
export function firstTier(stepValue: string): z.infer<typeof tierSchema> {
  return { minQty: '1', maxQty: '10', value: stepValue };
}

/** A tier-set issue, addressed to the whole set or a specific tier field. */
export type TierIssue =
  | { path: ['set']; message: string }
  | { path: ['minQty' | 'maxQty' | 'value', number]; message: string };

/** Pure tier-set validation: per-tier value/range + set-level gap/overlap/ascending + limit-to-original
 *  cap. Empty fields are already caught by `tierSchema`, so those rows are skipped here. Drives both the
 *  Create gate (via the create-schema superRefine) and the inline tier errors. */
export function validateTierSet(
  tiers: z.infer<typeof tierSchema>[],
  opts: { stepValue: string; tierBehavior: z.infer<typeof tierBehaviorSchema> },
): TierIssue[] {
  const issues: TierIssue[] = [];
  const rows = tiers.map((t) => ({
    min: Number(t.minQty),
    max: Number(t.maxQty),
    val: Number(t.value),
  }));
  const limit = Number(opts.stepValue);

  tiers.forEach((t, i) => {
    if (!t.minQty || !t.maxQty || !t.value) return; // required errors come from tierSchema
    const r = rows[i];
    if (!Number.isNaN(r.max) && !Number.isNaN(r.min) && r.max < r.min) {
      issues.push({
        path: ['maxQty', i],
        message: 'Max Qty must be greater than or equal to Min Qty',
      });
    }
    if (Number.isNaN(r.val) || r.val <= 0) {
      issues.push({ path: ['value', i], message: 'Value must be greater than 0' });
    } else if (opts.tierBehavior === 'limit_to_original' && !Number.isNaN(limit) && r.val > limit) {
      issues.push({
        path: ['value', i],
        message: `Value cannot exceed the discount value of ${opts.stepValue}`,
      });
    }
  });

  for (let i = 1; i < rows.length; i++) {
    const prev = rows[i - 1];
    const cur = rows[i];
    if ([prev.min, prev.max, cur.min].some(Number.isNaN)) continue;
    if (cur.min <= prev.max) {
      issues.push({ path: ['set'], message: `Tiers ${i} and ${i + 1} overlap` });
    } else if (cur.min !== prev.max + 1) {
      issues.push({
        path: ['set'],
        message: `Quantities ${prev.max + 1}-${cur.min - 1} are not covered`,
      });
    }
  }
  return issues;
}

/** Full create form (Step 1 + Step 2 + Step 3).
 *  `trigger` is nullable at the form level: a breaking Step-1 change (→ Customer while First Time
 *  Customer was chosen) clears it to `null` so the admin must re-select — the refine below re-requires it.
 *  `quantityRule` is required (seeded to Fixed in the form's defaultValues, like `trigger`) — kept
 *  required-key / no `.default()` so the zod input and output types match (RHF resolver invariant). */
export const discountCreateSchema = z
  .object({
    ...step1Fields,
    trigger: triggerSchema.nullable(),
    quantityRule: quantityRuleSchema,
  })
  .superRefine((v, ctx) => {
    valueSuperRefine(v, ctx);
    if (v.trigger == null) {
      ctx.addIssue({ code: 'custom', path: ['trigger'], message: 'Please select a trigger' });
    } else if (isFirstTimeTriggerForCustomer(v.applicability.type, v.trigger.type)) {
      ctx.addIssue({
        code: 'custom',
        path: ['trigger'],
        message: 'First Time Customer is not available for customer discounts',
      });
    }
    if (v.quantityRule.type === 'quantity_based') {
      for (const iss of validateTierSet(v.quantityRule.tiers, {
        stepValue: v.value,
        tierBehavior: v.quantityRule.tierBehavior,
      })) {
        const path =
          iss.path[0] === 'set'
            ? ['quantityRule', 'tiers']
            : ['quantityRule', 'tiers', iss.path[1], iss.path[0]];
        ctx.addIssue({ code: 'custom', path, message: iss.message });
      }
    }
  });

export function defaultApplicableTo(type: ApplicableToFormValue['type']): ApplicableToFormValue {
  if (type === 'device') return { type: 'device', devices: { all: false, ids: [] } };
  if (type === 'bundle') return { type: 'bundle', bundles: { all: false, ids: [] } };
  return { type: 'all_products' };
}

export function defaultApplicability(type: ApplicabilityFormValue['type']): ApplicabilityFormValue {
  if (type === 'device') return { type: 'device', devices: { all: false, ids: [] } };
  if (type === 'bundle') return { type: 'bundle', bundles: { all: false, ids: [] } };
  return {
    type: 'customer',
    customers: { all: false, ids: [] },
    applicableTo: { type: 'all_products' },
  };
}
